import { useEffect, useRef } from 'react';
import { faComments } from '@fortawesome/pro-regular-svg-icons';
import { faArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { EmptyState } from '@client/new/components/EmptyState/EmptyState';
import { Button } from '@design-system/components/button/button';
import { Typography } from '@design-system/components/typography/typography';
import { useCommunityFeed } from '../api/useCommunityFeed';
import { useCommunityPinnedPosts } from '../api/useCommunityPinnedPosts';
import { useToggleReaction } from '../api/useToggleReaction';
import { useNewPostsIndicator } from '../lib/useNewPostsIndicator';
import { analytics } from '../lib/analytics';
import { useCommunityContext } from '../lib/communityContext';
import { CommunityId } from '../model/community.types';
import { FeedAuthorFilter } from '../model/feedFilter.types';
import { ReactionParent } from '../model/post.types';
import { PostCard } from './PostCard';
import { PostCardSkeleton } from './PostCardSkeleton';
import { FeedLoadMore } from './FeedLoadMore';

type Props = {
  filter?: FeedAuthorFilter;
  search?: string;
};

const FeedSkeleton = () => (
  <div
    data-testid="community-feed-skeleton"
    aria-busy="true"
    className="flex w-full flex-col divide-y divide-[#EEEEEE] rounded-[10px] bg-white p-4"
  >
    {Array.from({ length: 3 }, (_, index) => (
      <PostCardSkeleton key={index} />
    ))}
  </div>
);

export const CommunityFeed = ({ filter = 'all', search = '' }: Props) => {
  const { communityId, influencerId, canModerate } = useCommunityContext();
  const { posts, hasMore, isLoadingMore, loadMore } = useCommunityFeed(
    communityId,
    canModerate,
    filter,
    influencerId,
    search,
  );
  const pinned = useCommunityPinnedPosts(communityId, canModerate);
  const pinnedCount = pinned.type === 'fetched' ? pinned.value.length : 0;
  const toggleReaction = useToggleReaction(communityId);
  const isDefaultFeed = filter === 'all' && search.trim().length === 0;
  const { hasNewPosts, onLoadNewPosts } = useNewPostsIndicator(
    communityId,
    posts,
    isDefaultFeed,
  );

  // Fire once per community when its default feed first loads, mirroring the
  // legacy "Community Viewed" on first-page load.
  const viewedCommunityRef = useRef<CommunityId | null>(null);
  useEffect(() => {
    if (posts.type !== 'fetched' || !isDefaultFeed) return;
    if (viewedCommunityRef.current === communityId) return;
    viewedCommunityRef.current = communityId;
    analytics.trackCommunityViewed(posts.value);
  }, [posts, isDefaultFeed, communityId]);

  const handleLoadNewPosts = () => {
    analytics.trackNewPostsIndicatorClicked();
    onLoadNewPosts();
  };

  const handleLoadMore = () => {
    analytics.trackMoreFeedLoaded();
    loadMore();
  };

  switch (posts.type) {
    case 'not-fetched':
    case 'fetching':
      return <FeedSkeleton />;
    case 'fetch-error':
      return (
        <div
          role="alert"
          className="flex w-full flex-col items-center gap-2 py-10 text-center text-[#8F94A1]"
        >
          <Typography as="p" variant="body" size="medium">
            We couldn&apos;t load this feed. Please try again.
          </Typography>
        </div>
      );
    case 'fetched':
      return posts.value.length === 0 ? (
        <EmptyState
          icon={faComments}
          captionLine1="No posts yet"
          captionLine2="Posts shared in this community will show up here"
        />
      ) : (
        <div className="relative flex w-full flex-col">
          {hasNewPosts && (
            <div className="pointer-events-none sticky top-4 z-10 -mb-12 flex h-12 items-start justify-center">
              <Button
                color="primary"
                leftIcon={faArrowUp}
                onClick={handleLoadNewPosts}
                className="pointer-events-auto rounded-full shadow-md"
                testId="CommunityFeedNewPosts"
              >
                New Posts
              </Button>
            </div>
          )}
          <div className="flex w-full flex-col divide-y divide-[#EEEEEE] rounded-[10px] bg-white p-4">
            {posts.value.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                pinnedCount={pinnedCount}
                onToggleReaction={(type) =>
                  toggleReaction({
                    parent: ReactionParent.Post,
                    entityId: post.id,
                    type,
                  })
                }
              />
            ))}
            {hasMore && (
              <FeedLoadMore
                onLoadMore={handleLoadMore}
                isLoadingMore={isLoadingMore}
              />
            )}
          </div>
        </div>
      );
  }
};
